const clamp = (value, minimum, maximum) => (
  Math.max(minimum, Math.min(value, maximum))
);

export const clampCropToBounds = (crop) => {
  const width = clamp(Number(crop.width) || 0, 1, 100);
  const height = clamp(Number(crop.height) || 0, 1, 100);
  return {
    x: clamp(Number(crop.x) || 0, 0, 100 - width),
    y: clamp(Number(crop.y) || 0, 0, 100 - height),
    width,
    height,
  };
};

export const createCenteredCrop = ({
  displayWidth,
  displayHeight,
  aspect,
}) => {
  if (!aspect) {
    return { x: 5, y: 5, width: 90, height: 90 };
  }
  let width = 90;
  let height = width * displayWidth / aspect / displayHeight;
  if (height > 90) {
    height = 90;
    width = height * displayHeight * aspect / displayWidth;
  }
  return {
    x: (100 - width) / 2,
    y: (100 - height) / 2,
    width,
    height,
  };
};

export const resizeCropFromBottomRight = ({
  crop,
  deltaXPercent,
  deltaYPercent,
  displayWidth,
  displayHeight,
  aspect,
}) => {
  if (!aspect) {
    return clampCropToBounds({
      ...crop,
      width: clamp(crop.width + deltaXPercent, 5, 100 - crop.x),
      height: clamp(crop.height + deltaYPercent, 5, 100 - crop.y),
    });
  }
  const maxWidthForHeight = (
    (100 - crop.y) * displayHeight * aspect / displayWidth
  );
  const verticalDeltaAsWidth = (
    deltaYPercent * displayHeight * aspect / displayWidth
  );
  const widthDelta = Math.abs(deltaXPercent) >= Math.abs(verticalDeltaAsWidth)
    ? deltaXPercent
    : verticalDeltaAsWidth;
  const width = clamp(
    crop.width + widthDelta,
    5,
    Math.min(100 - crop.x, maxWidthForHeight),
  );
  return {
    ...crop,
    width,
    height: width * displayWidth / aspect / displayHeight,
  };
};

const getPageProtocol = () => (
  typeof window === 'undefined' ? '' : window.location.protocol
);

const normalizeImageRequestUrl = (url, pageProtocol) => {
  if (pageProtocol !== 'https:') return url;
  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'http:') parsed.protocol = 'https:';
    return parsed.toString();
  } catch (_error) {
    return url;
  }
};

export const fetchJianyingImageBlob = async (
  url,
  {
    fetchImpl,
    pageProtocol = getPageProtocol(),
  } = {},
) => {
  const requestUrl = normalizeImageRequestUrl(url, pageProtocol);
  const requestImage = fetchImpl || window.fetch.bind(window);
  let response;
  try {
    response = await requestImage(requestUrl, {
      cache: 'no-store',
      credentials: 'omit',
      mode: 'cors',
    });
  } catch (_error) {
    throw new Error(
      '图片读取失败：浏览器无法访问图片，请检查链接有效性、HTTPS 和 OSS 跨域配置',
    );
  }
  if (!response?.ok) {
    throw new Error(`图片读取失败（HTTP ${response?.status || 0}），图片链接可能已过期或无权访问`);
  }
  const blob = await response.blob();
  if (!blob?.type?.startsWith('image/')) {
    throw new Error('图片读取失败：链接返回的内容不是图片');
  }
  return blob;
};

const decodeImage = (url) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('图片解码失败，文件可能已损坏或格式不受支持'));
  image.src = url;
});

const loadImage = async (url) => {
  const blob = await fetchJianyingImageBlob(url);
  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = await decodeImage(objectUrl);
    return {
      image,
      release: () => URL.revokeObjectURL(objectUrl),
    };
  } catch (error) {
    URL.revokeObjectURL(objectUrl);
    throw error;
  }
};

const canvasToBlob = (canvas, contentType = 'image/jpeg', quality = 0.95) => (
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('浏览器生成裁剪图片失败'));
    }, contentType, quality);
  })
);

export const getJianyingRotationPlan = ({ rotation, width, height }) => {
  const sourceWidth = Number(width);
  const sourceHeight = Number(height);
  if (!(sourceWidth > 0) || !(sourceHeight > 0)) {
    throw new Error('图片尺寸无效，无法旋转');
  }
  const numericRotation = Number(rotation || 0);
  const signedRotation = (
    ((numericRotation + 180) % 360 + 360) % 360 - 180
  );
  const quarterPosition = signedRotation / 90;
  const unwrappedQuarterTurns = quarterPosition >= 0
    ? Math.floor(quarterPosition + 0.5 - Number.EPSILON)
    : Math.ceil(quarterPosition - 0.5 + Number.EPSILON);
  const quarterTurns = (
    (unwrappedQuarterTurns % 4) + 4
  ) % 4;
  const quarterRotation = quarterTurns * 90;
  const fineRotationValue = signedRotation - unwrappedQuarterTurns * 90;
  const fineRotation = Math.abs(fineRotationValue) < 1e-9
    ? 0
    : fineRotationValue;
  return {
    rotation: signedRotation,
    quarterTurns,
    quarterRotation,
    fineRotation,
    outputWidth: sourceWidth,
    outputHeight: sourceHeight,
    coverScale: 1,
  };
};

const drawRotatedImage = (image, rotation) => {
  const plan = getJianyingRotationPlan({
    rotation,
    width: image.naturalWidth,
    height: image.naturalHeight,
  });
  const canvas = document.createElement('canvas');
  canvas.width = plan.outputWidth;
  canvas.height = plan.outputHeight;
  const context = canvas.getContext('2d');
  context.translate(canvas.width / 2, canvas.height / 2);
  context.rotate(plan.rotation * Math.PI / 180);
  context.drawImage(
    image,
    -image.naturalWidth / 2,
    -image.naturalHeight / 2,
  );
  return canvas;
};

const cropCanvasToBlob = async (sourceCanvas, crop) => {
  const sourceX = Math.round(sourceCanvas.width * crop.x / 100);
  const sourceY = Math.round(sourceCanvas.height * crop.y / 100);
  const sourceWidth = Math.max(1, Math.round(sourceCanvas.width * crop.width / 100));
  const sourceHeight = Math.max(1, Math.round(sourceCanvas.height * crop.height / 100));
  const canvas = document.createElement('canvas');
  canvas.width = Math.min(sourceWidth, sourceCanvas.width - sourceX);
  canvas.height = Math.min(sourceHeight, sourceCanvas.height - sourceY);
  canvas.getContext('2d').drawImage(
    sourceCanvas,
    sourceX,
    sourceY,
    canvas.width,
    canvas.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );
  return canvasToBlob(canvas);
};

export const rotateJianyingImageToBlob = async (url, rotation) => {
  const { image, release } = await loadImage(url);
  try {
    return await canvasToBlob(drawRotatedImage(image, rotation));
  } finally {
    release();
  }
};

export const cropRotatedJianyingImageToBlob = async (url, crop, rotation) => {
  const { image, release } = await loadImage(url);
  try {
    return await cropCanvasToBlob(drawRotatedImage(image, rotation), crop);
  } finally {
    release();
  }
};

export const cropJianyingImageToBlob = async (url, crop) => {
  const { image, release } = await loadImage(url);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    canvas.getContext('2d').drawImage(
      image,
      0,
      0,
      canvas.width,
      canvas.height,
    );
    return await cropCanvasToBlob(canvas, crop);
  } finally {
    release();
  }
};
